/**
 * Restart HAND poses. (Law 15; §5.1 restarts, §2.8 anim)
 *
 * Owner report, verbatim: *"in throw in the ball has to be taken with hand there
 * is no aniamtion for that"*.
 *
 * ── Why this is a separate stage and not two lines inside rules/fsm.ts ───────
 *
 * The FSM runs at step.ts stage 2. player-controller's `stepAnim` runs at stage
 * 4 and RE-DERIVES `p.anim` from velocity for every active player, every tick.
 * So an anim the FSM stamps is overwritten LATER THE SAME TICK and never reaches
 * the renderer at all — a throw-in taker stood at the touchline reads as
 * `AnimState.Idle`, which is exactly the reported "there is no animation for
 * that". `AnimState.Throw` and `THROW_ANIM_HOLD_S` have both existed unused
 * since the first types.ts (the constant's own comment says it "drives
 * AnimState.Throw"); the member was authored for this and only the wiring was
 * ever missing, and it could not have worked from stage 2 in any case.
 *
 * This module therefore runs at stage 5b — AFTER stage 4, so it gets the last
 * word on the pose, which is the identical seam goalkeeper.ts relies on and
 * documents ("we run at pipeline stage 5, AFTER player control (4)... we get the
 * last word on the GK's anim").
 *
 * ── Everything here is DERIVED. No new state. ───────────────────────────────
 *
 * There is no `throwInAnimTimer` and there must not be: it would have to be
 * cloned, hashed and probably serialised, and world.ts warns exactly what
 * happens when one of those is missed. Every pose below is read from fields that
 * ALREADY clone and hash — `match.phase`, `match.restartStage`,
 * `match.restartTaker`, `match.restartX/Z`, `ball.px/py/pz`,
 * `ball.lastTouchedBy`, `ball.lastTouchTick` — so it survives a reconciliation
 * rewind for free, in the same way goalkeeper.ts derives the reaction gate and
 * the own-touch refractory rather than storing them. (§2.4, §15.2, D1)
 *
 * Zero allocation, zero RNG draws (§15.3, §15.7).
 */

import {
  AnimState,
  MatchPhase,
  MAX_PLAYERS,
  RestartStage,
  type WorldState,
} from './types.js';
import { PLAYER_CAPSULE_HEIGHT, THROW_TAKE_REACH } from './constants.js';
import { setAnimState } from './anim-exec.js';

/** Called from step.ts stage 5b, after player control has re-derived every anim. */
export function stepRestartAnim(state: WorldState): void {
  const taker = state.match.restartTaker;
  if (taker < 0 || taker >= MAX_PLAYERS) return;
  const p = state.players;
  if (p.active[taker] !== 1 || p.sentOff[taker] === 1) return;

  // rules/fsm.ts arms the authored release before detaching the ball. Player
  // control re-derives locomotion earlier in this tick, so reassert the release
  // pose throughout that deterministic contact countdown.
  if (
    state.match.phase === MatchPhase.ThrowIn &&
    state.match.restartStage === RestartStage.Ready &&
    p.actionTimer[taker]! > 0
  ) {
    setAnim(state, taker, AnimState.Throw);
    return;
  }

  if (isThrowInDeliveryTick(state, taker)) {
    setAnim(state, taker, AnimState.Throw);
    return;
  }
  if (isHoldingForThrowIn(state, taker)) {
    setAnim(state, taker, AnimState.ThrowInHold);
  }
}

/**
 * The taker is STOOD AT THE LINE with the ball in both hands, above his head.
 *
 * This is the long, visible half of a throw-in: he walks to the spot through
 * Setup (RESTART_STAGE_SPEED, §5.1) and then waits out the Ready window, which
 * for a throw-in measures ~7 s in a real match (SESSION_LOG session 33). For all
 * of it he was rendering the locomotion ladder's Idle.
 *
 * Gated on ARRIVAL, not on the stage, so he is still walking (and rendered
 * walking) while he fetches the ball and only picks it up once he is there.
 * THROW_TAKE_REACH is the same radius rules/fsm.ts's runThrowInReady uses to
 * decide he is close enough to take it, so the pose and the rule agree by
 * construction rather than by a second tuned number.
 *
 * ── …and gated on the BALL being off the turf (owner, 2026-08-09) ────────────
 *
 * Arrival alone used to be the whole test, which meant the taker held a
 * ball-in-both-hands pose for the entire Setup window while the real ball was
 * still lying on the grass at his feet, pinned there by placeBallForRestart.
 * rules/fsm.ts now runs a real pickup (runThrowInPickup) in the last
 * THROW_PICKUP_S of Setup, so there is finally a fact to gate on: he is holding
 * it when it is in his hands, and not one tick before. Reading `ball.grounded`
 * rather than a new flag keeps this module's no-new-state rule (see header).
 */
function isHoldingForThrowIn(state: WorldState, taker: number): boolean {
  const m = state.match;
  if (m.phase !== MatchPhase.ThrowIn) return false;
  // Live would mean the throw has already been taken.
  if (m.restartStage !== RestartStage.Setup && m.restartStage !== RestartStage.Ready) return false;
  // Still on the deck: he has not picked it up yet, whatever else is true.
  if (state.ball.grounded === 1) return false;
  const p = state.players;
  const dx = p.px[taker]! - m.restartX;
  const dz = p.pz[taker]! - m.restartZ;
  return dx * dx + dz * dz <= THROW_TAKE_REACH * THROW_TAKE_REACH;
}

/**
 * THE RELEASE — this exact tick, the ball left his hands from above his head.
 *
 * Fingerprinted rather than flagged, so this module needs no edit to
 * rules/fsm.ts and cannot be broken by one. Four facts must hold at once, and
 * together they are unique to `executeThrowIn` having fired at stage 2 of THIS
 * tick:
 *
 *   1. he is the designated restart taker, and the restart has just gone Live;
 *   2. the ball's last touch is HIM, on THIS tick;
 *   3. the ball is ABOVE HEAD HEIGHT (PLAYER_CAPSULE_HEIGHT) — Law 15's "from
 *      behind and over the head". This is the same head-height test ball.ts uses
 *      to tell a header from a footed strike;
 *   4. the ball is still ON THE RESTART SPOT horizontally. It has to be: stage 7
 *      (ball integration) has not run yet, and the FSM re-places the ball on the
 *      spot every Setup/Ready tick.
 *
 * Why a footed restart cannot match it: every foot strike is applied by
 * resolveKicks at stage 6, LATER in the tick than this stage, so a corner or
 * free-kick taker's `lastTouchTick` is still stale when we look. Why an ordinary
 * open-play touch cannot: a dribble knock or poke leaves the ball at boot height
 * and metres from the last restart spot. Why a keeper's release cannot: his ball
 * leaves at GK_HAND_HEIGHT (0.9 m), below head height, and from his own box.
 *
 * If any of those ever drifts, this returns false and the taker simply keeps the
 * `ThrowInHold` pose through the release — a worse animation, never a wrong
 * simulation. The pose is non-authoritative; nothing in /sim branches on it.
 */
function isThrowInDeliveryTick(state: WorldState, taker: number): boolean {
  const m = state.match;
  const b = state.ball;
  if (m.phase !== MatchPhase.InPlay || m.restartStage !== RestartStage.Live) return false;
  if (b.lastTouchedBy !== taker || b.lastTouchTick !== state.tick) return false;
  if (b.py <= PLAYER_CAPSULE_HEIGHT) return false;
  const dx = b.px - m.restartX;
  const dz = b.pz - m.restartZ;
  return dx * dx + dz * dz <= THROW_TAKE_REACH * THROW_TAKE_REACH;
}

/** Same semantics as every other setAnim in /sim: a change resets the blend —
 *  and, since §11, stamps a new `animExecId`. Delegated rather than repeated so
 *  this module cannot drift from the shared definition of "a state changed". */
function setAnim(state: WorldState, id: number, anim: AnimState): void {
  setAnimState(state.players, id, anim);
}
